import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage, useIntl} from "react-intl";

import {Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";

import BeatLoaderMini from "components/loaders/BeatLoaderMini";

import {searchProducts} from "api/personalShopper";

import {black500, blue50, greyishBlue100, greyishBlue600} from "assets/jss/main";

import {formatMoney} from "views/personalShopper/components/formatters";

const GENDERS = ["", "woman", "man", "girl", "boy", "baby", "unisex"];
const MAX_SELECTION = 12;

ProductPickerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onSend: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

/**
 * The shop's catalogue as the assistant searches it: the member types a product type and a
 * few filters, ticks the products, and the cards leave in that order, signed by them.
 */
function ProductPickerDialog({open, onSend, onClose}) {
    const intl = useIntl();
    const [criteria, setCriteria] = useState({query: "", gender: "", brands: "", sizes: "", priceMax: ""});
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState([]);
    const [searching, setSearching] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const search = () => {
        if (criteria.query.trim() === "" || searching) {
            return;
        }
        setSearching(true);
        setError(null);
        searchProducts({...criteria, query: criteria.query.trim(), priceMax: criteria.priceMax ? Math.round(Number(criteria.priceMax) * 100) : ""})
            .then((data) => setProducts(data.items || []))
            .catch((requestError) => setError(requestError.message))
            .finally(() => setSearching(false));
    };

    const toggle = (code) => setSelected((previous) => (previous.includes(code) ? previous.filter((item) => item !== code) : (previous.length < MAX_SELECTION ? [...previous, code] : previous)));

    const send = () => {
        if (selected.length === 0 || sending) {
            return;
        }
        setSending(true);
        setError(null);
        onSend(selected)
            .then(() => {
                setSelected([]);
                onClose();
            })
            .catch((requestError) => setError(requestError.message))
            .finally(() => setSending(false));
    };

    return (
        <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle><FormattedMessage id="personalShopper.products.dialog.title" /></DialogTitle>
            <DialogContent>
                <div style={{display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: "12px"}}>
                    <TextField
                        autoFocus
                        size="small"
                        placeholder={intl.formatMessage({id: "personalShopper.products.dialog.query"})}
                        value={criteria.query}
                        onChange={(event) => setCriteria({...criteria, query: event.target.value})}
                        onKeyDown={(event) => event.key === "Enter" && search()}
                        sx={{flex: "2 1 200px"}}
                    />
                    <TextField select size="small" value={criteria.gender} onChange={(event) => setCriteria({...criteria, gender: event.target.value})} sx={{minWidth: "130px"}}>
                        {GENDERS.map((gender) => (
                            <MenuItem key={gender || "all"} value={gender}>
                                <FormattedMessage id={`personalShopper.products.dialog.gender.${gender || "all"}`} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField size="small" placeholder={intl.formatMessage({id: "personalShopper.products.dialog.brands"})} value={criteria.brands} onChange={(event) => setCriteria({...criteria, brands: event.target.value})} sx={{flex: "1 1 120px"}} />
                    <TextField size="small" placeholder={intl.formatMessage({id: "personalShopper.products.dialog.sizes"})} value={criteria.sizes} onChange={(event) => setCriteria({...criteria, sizes: event.target.value})} sx={{width: "110px"}} />
                    <TextField size="small" type="number" placeholder={intl.formatMessage({id: "personalShopper.products.dialog.priceMax"})} value={criteria.priceMax} onChange={(event) => setCriteria({...criteria, priceMax: event.target.value})} sx={{width: "120px"}} />
                    <Button variant="outlined" startIcon={<SearchIcon />} disabled={searching || criteria.query.trim() === ""} onClick={search} sx={{textTransform: "none"}}>
                        <FormattedMessage id="personalShopper.products.dialog.search" />
                        <BeatLoaderMini loading={searching} />
                    </Button>
                </div>
                {error && <Alert severity="error" sx={{marginBottom: "12px"}}>{error}</Alert>}
                {!searching && products.length === 0 && (
                    <div style={{color: greyishBlue600, fontSize: "13px", padding: "16px 0"}}><FormattedMessage id="personalShopper.products.dialog.empty" /></div>
                )}
                <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "10px"}}>
                    {products.map((product) => {
                        const checked = selected.includes(product.code);

                        return (
                            <div
                                key={product.code}
                                role="button"
                                tabIndex={0}
                                onClick={() => toggle(product.code)}
                                onKeyDown={(event) => event.key === "Enter" && toggle(product.code)}
                                style={{border: `1px solid ${checked ? "#0052cb" : greyishBlue100}`, backgroundColor: checked ? blue50 : "#ffffff", borderRadius: "8px", padding: "8px", cursor: "pointer", position: "relative"}}
                            >
                                <Checkbox size="small" checked={checked} sx={{position: "absolute", top: 0, right: 0}} tabIndex={-1} />
                                {product.imageUrl
                                    ? <img src={product.imageUrl} alt="" style={{width: "100%", height: "140px", objectFit: "cover", borderRadius: "6px"}} />
                                    : <div style={{width: "100%", height: "140px", borderRadius: "6px", backgroundColor: "#F4F4F6"}} />}
                                <div style={{fontSize: "12px", color: greyishBlue600, marginTop: "6px"}}>{product.brand}</div>
                                <div style={{fontSize: "13px", fontWeight: 600, color: black500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{product.name}</div>
                                <div style={{fontSize: "13px"}}>
                                    {formatMoney(product.price, product.currencyCode)}
                                    {product.originalPrice && product.originalPrice > product.price && (
                                        <span style={{color: greyishBlue600, textDecoration: "line-through", marginLeft: "6px", fontSize: "12px"}}>{formatMoney(product.originalPrice, product.currencyCode)}</span>
                                    )}
                                </div>
                                {(product.sizes || []).length > 0 && <div style={{fontSize: "11px", color: greyishBlue600}}>{product.sizes.join(" · ")}</div>}
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
            <DialogActions>
                <span style={{color: greyishBlue600, fontSize: "12px", marginRight: "auto", paddingLeft: "16px"}}>
                    <FormattedMessage id="personalShopper.products.dialog.selected" values={{count: selected.length, max: MAX_SELECTION}} />
                </span>
                <Button onClick={onClose} disabled={sending} sx={{textTransform: "none"}}>
                    <FormattedMessage id="personalShopper.products.dialog.cancel" />
                </Button>
                <Button variant="contained" startIcon={<SendIcon />} disabled={sending || selected.length === 0} onClick={send} sx={{textTransform: "none"}}>
                    <FormattedMessage id="personalShopper.products.dialog.send" />
                    <BeatLoaderMini loading={sending} />
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProductPickerDialog;
